import React, { useCallback, useMemo } from 'react';
import FieldVisibilityDropdown from './FieldVisibilityDropdown';
import KanbanFilterDropdown from './KanbanFilterDropdown/index';
import { ToolbarBtn } from './FlowToolbar/ToolbarButton';
import FokusButton from '../shared/FokusButton';
import { calculateLockStates } from '../FlowchartView/utils/dependencyUtils';
import { isTaskActionable } from '../shared/utils/taskAvailabilityUtils';
import {
  MissionIcon,
  DiamondIcon,
  HourglassIcon,
  RecentreIcon,
  SearchIcon,
  ResponsiveToolbar,
} from '../shared/ToolbarPrimitives';
import ResetCampaignToolbarItem from './ResetCampaignToolbarItem';
import VariablesToolbarItem from './VariablesToolbarItem';

/**
 * Toolbar for Neural view — mirrors shared “create + search” affordances until the view grows.
 */
const NeuralToolbar = ({
  onAddTask,
  onAddDecision,
  onAddWait,
  onOpenMissionSearch,
  isMultiCampaignMode = false,
  onOpenCampaignPicker,
  cardDisplaySettings,
  onCardDisplaySettingsChange,
  customFields = [],
  onManageCustomFields,
  filters,
  onFiltersChange,
  statuses,
  categories,
  allTags,
  tasks,
  showNestedCampaigns = true,
  onShowNestedCampaignsChange,
  nestedSprints = [],
  showActionableOnly = false,
  onToggleActionableOnly,
  // FOKUS mode props (same contract as Kanban/Table/Gantt/Todo toolbars)
  isFokusMode = false,
  selectedTaskIds = new Set(),
  onFokusSelected,
  onExitFokusMode,
  onFokusByFilter,
  onResetCampaignProgress,
  onManageVariables = null,
  neuralViewApiRef,
}) => {
  const lockStates = useMemo(
    () => calculateLockStates(tasks, nestedSprints, statuses),
    [tasks, nestedSprints, statuses],
  );
  const actionableTaskIds = useMemo(
    () => tasks
      .filter((task) => isTaskActionable(task, lockStates, nestedSprints, statuses))
      .map((task) => task.id),
    [tasks, lockStates, nestedSprints, statuses],
  );
  const handleAddMission = useCallback(() => {
    if (isMultiCampaignMode && onOpenCampaignPicker) {
      onOpenCampaignPicker();
    } else {
      onAddTask?.();
    }
  }, [isMultiCampaignMode, onOpenCampaignPicker, onAddTask]);

  const searchIconEl = useMemo(() => <SearchIcon />, []);

  return (
    <ResponsiveToolbar>
      <ResponsiveToolbar.Item priority={1}>
        <div className="unified-toolbar__group">
          <ToolbarBtn
            icon={<MissionIcon />}
            label="Add Mission"
            onClick={handleAddMission}
            title="Add new mission"
            variant="accent"
          />
          <ToolbarBtn
            icon={<DiamondIcon />}
            label="Add Decision"
            onClick={onAddDecision}
            title="Add decision node"
          />
          <ToolbarBtn
            icon={<HourglassIcon />}
            label="Add Wait for"
            onClick={onAddWait}
            title="Add a wait-for card"
          />
        </div>
      </ResponsiveToolbar.Item>
      <ResponsiveToolbar.Item priority={2}>
        <FokusButton
          isFocusMode={isFokusMode}
          hasSelection={selectedTaskIds.size > 0}
          selectedCount={selectedTaskIds.size}
          actionableTaskIds={actionableTaskIds}
          onFocusSelected={(ids) => onFokusSelected?.(ids)}
          onExitFocusMode={onExitFokusMode}
          onFilterFocus={onFokusByFilter}
          tasks={tasks}
          categories={categories}
          statuses={statuses}
          allTags={allTags}
          lockStates={lockStates}
          nestedSprints={nestedSprints}
          selectionLabel="Selected Mission"
        />
      </ResponsiveToolbar.Item>
      <ResponsiveToolbar.Item priority={3}>
        <KanbanFilterDropdown
          filters={filters}
          onFiltersChange={onFiltersChange}
          statuses={statuses}
          categories={categories}
          allTags={allTags}
          tasks={tasks}
          showNestedCampaigns={showNestedCampaigns}
          onShowNestedCampaignsChange={onShowNestedCampaignsChange}
          lockStates={lockStates}
          nestedSprints={nestedSprints}
          showActionableOnly={showActionableOnly}
          onToggleActionableOnly={onToggleActionableOnly}
          customFields={customFields}
        />
      </ResponsiveToolbar.Item>
      <ResponsiveToolbar.Item priority={7}>
        <ToolbarBtn
          icon={<RecentreIcon />}
          label="Re-centre view"
          onClick={() => neuralViewApiRef?.current?.recentre()}
          title="Frame the whole graph"
        />
      </ResponsiveToolbar.Item>
      <ResponsiveToolbar.Item priority={9}>
        <FieldVisibilityDropdown
          view="neural"
          visibility={cardDisplaySettings}
          onVisibilityChange={onCardDisplaySettingsChange}
          customFields={customFields}
          onManageCustomFields={onManageCustomFields}
        />
      </ResponsiveToolbar.Item>
      {onResetCampaignProgress && (
        <ResponsiveToolbar.Item priority={11} label="Reset campaign">
          <ResetCampaignToolbarItem onResetCampaignProgress={onResetCampaignProgress} />
        </ResponsiveToolbar.Item>
      )}
      {onManageVariables && (
        <ResponsiveToolbar.Item priority={12} label="Variables">
          <VariablesToolbarItem onManageVariables={onManageVariables} />
        </ResponsiveToolbar.Item>
      )}
      <ResponsiveToolbar.Item priority={4}>
        <ToolbarBtn
          icon={searchIconEl}
          label="Search"
          onClick={onOpenMissionSearch}
          title="Search missions (Ctrl+F)"
        />
      </ResponsiveToolbar.Item>
    </ResponsiveToolbar>
  );
};

export default NeuralToolbar;
